import React, { useState, useRef, useEffect } from 'react';
import { connect } from 'react-redux'
import { CSVLink } from "react-csv";
import { useReactTable, getCoreRowModel } from '@tanstack/react-table'
import moment from 'moment';
import EventDownload from '../components/EventDownload';
import RealtimeEvents from '../components/RealtimeEvents';
import UserLog from '../components/UserLog';
import { Card, Drawer } from '../components/ui'
import { setEventState, resetEventState, EVENT_POLL_MS, getEvents } from '../slices/EventSlice';
import { getGates } from '../slices';
import { useLivePoll } from '../hooks/useLivePoll';
import './TransactionMonitor.css'

/*
 * TransactionMonitor — Console reskin of the live Event Monitor screen.
 *
 * PRESENTATION ONLY. Preserved verbatim from the previous version:
 *   - the CSV download state machine (the eventDownloadStatus useEffect +
 *     react-csv CSVLink + EventDownload modal),
 *   - the onsite-count wiring (setOnsiteCountId / setOnsiteCountBySite and the
 *     gate|site toggle + selector that drive getOnsiteCount — via App props),
 *   - the Reset List plumbing (toggleReset -> resetEventState(true)),
 *   - the full Redux mapStateToProps shape.
 * Only the markup/classes changed (stat Cards + toolbar buttons). The 3-min
 * incremental poll and poll-abort live in EventSlice/App and are untouched.
 *
 * The page title ("Event Monitor") + location breadcrumb are rendered by the
 * AppShell topbar, so they're not repeated here.
 */
const TransactionMonitor = (props) => {

  const csvButtonRef = useRef()
  const [showDownloadModal, setShowDownloadModal] = useState(false)
  const [csvDownloadData, setCsvDownloadData] = useState([])
  const [reset, setReset] = useState(false)
  // [86bbwu3px item 5] which view is showing under Event Monitor: the live crossings feed, or the
  // User Log (session/auth activity). Backed by Redux (monitorView, persisted) so a HARD REFRESH
  // restores the view instead of snapping back to Gate Events.
  const view = props.monitorView || 'events'
  const setView = (v) => props.setEventState({ field: 'monitorView', value: v })
  // [86bbumedw] P3 — on-demand on-site detail: which site's gate breakdown is open in the
  // slide-over (null = closed). Stored by id so it re-derives from the live list on each poll.
  const [onsiteDetailId, setOnsiteDetailId] = useState(null)

  const headers = ['Date/Time', 'Type', 'Gate', 'Customer', 'Company', 'Driver', 'LPN', 'Logged By']
  const columns = [
    { id: 'timestamp', accessorFn: row => moment(row.timestamp).format('MM/DD/YY hh:mm a') },
    { id: 'typeId', accessorFn: row => row.GateType?.sName },
    { id: 'gateId', accessorFn: row => row.Gate?.sName || 'UNASSIGNED' },
    { id: 'customerId', accessorFn: row => row.GateCustomer?.sName || 'UNASSIGNED' },
    { id: 'company', accessorFn: row => row.GateCompany?.sName || '-----' },
    { id: 'driver', accessorFn: row => row.GatePerson?.sFullName || '-----' },
    { id: 'lpn', accessorFn: row => row.GateLPN?.sName || '-----' },
    { id: 'guardId', accessorFn: row => row.GateUser?.sFirstName + row.GateUser?.sLastName },
  ]

  const table = useReactTable({
    data: props.eventDownloadData,
    columns: columns,
    getCoreRowModel: getCoreRowModel(),
  })

  useEffect(() => {
    const prepDownloadData = async () => {
      if (props.eventDownloadStatus === 'downloading') {
        if (props.eventDownloadData) {

          if (props.eventDownloadData.length > 0) {
            let parsedData = table.getRowModel().rows.map(row => row.getAllCells().map(cell => cell.getValue()))
            setCsvDownloadData(parsedData)
            await props.setEventState({field: 'eventDownloadStatus', value: 'ready'})

          } else {
            await props.setEventState({field: 'eventDownloadStatus', value: false})
            await props.setEventState({field: 'eventDownloadData', value: false})
            await props.setEventState({field: 'eventDownloadError', value: 'Unable to complete CSV Download. No data found matching the search criteria. Please make changes and try again.'})
            setShowDownloadModal(true)
          }
        }

      } else if (props.eventDownloadStatus === 'ready' && csvDownloadData && csvDownloadData.length > 0) {
        csvButtonRef.current.click()
        await props.setEventState({field: 'eventDownloadStatus', value: 'done'})

      } else if (props.eventDownloadStatus === 'done') {
        setTimeout(async () => {
          setCsvDownloadData([])
          await props.setEventState({field: 'eventDownloadStatus', value: false})
          await props.setEventState({field: 'eventDownloadData', value: false})
        }, 3000)
      }
    }

    prepDownloadData()

  // eslint-disable-next-line
  }, [props.eventDownloadStatus, props.eventDownloadData, csvDownloadData])

  // [86bbwu3px] ONE bulletproof live loop for the whole Event Monitor: the crossings feed
  // (getEvents) AND the on-site counts (getGates), on a single timer. Active only on the Events view
  // AND once the first page has loaded (eventListLoaded>0) — the latter keeps the loop's immediate
  // poll from racing getInitialData's page-1 load. It pauses/restarts cleanly with the view; on-site
  // counts pause while a detail slide-over is open (numbers don't shift under the reader) — the feed
  // keeps polling. useLivePoll survives errors, never double-polls, and catches up the instant the
  // tab is visible again.
  //
  // The FIRST tick after (re)entering Events is an authoritative page-1 REPLACE, not a delta poll:
  // the delta only PREPENDS, so re-entering from the User Log after many crossings arrived would
  // leave a gap and drift the total. firstReplace is armed whenever we leave Events and consumed by
  // that first tick. Folding it into the loop (vs a separate effect) means exactly ONE fetch on
  // re-entry, so it can't race the loop's own poll and inflate the "of N" total.
  const firstReplace = useRef(false)
  useEffect(() => { if (view !== 'events') firstReplace.current = true }, [view])
  const doPoll = async () => {
    const replace = firstReplace.current; firstReplace.current = false
    const tasks = [ replace ? props.getEvents({ filters: { page: 1 } })
                            : props.getEvents({ poll: true, filters: { page: 1 } }) ]
    if (onsiteDetailId == null) tasks.push(props.getGates())
    await Promise.all(tasks)
  }
  useLivePoll({ active: view === 'events' && props.eventListLoaded > 0, intervalMs: EVENT_POLL_MS, poll: doPoll })

  const toggleDownloadModal = async () => {
    const show = !showDownloadModal
    setShowDownloadModal(show)
    if (!show) {
      props.setEventState({field: 'eventDownloadError', value: ''})
    }
  }

  const toggleReset = async () => {
    const r = !reset
    setReset(r)
    if (r) {
      props.resetEventState(true)
    }
  }

  // ---- on-site breakdown: group gates (with their live on-site counts) by site,
  // non-zero only, sorted by headcount; plus a grand total. Source is the gates list
  // (each gate carries bPeopleCount/bVehicleCount + its GateSite), refreshed on mount.
  const gates = props.gates || []
  const siteMap = {}
  gates.forEach(g => {
    const p = parseInt(g.bPeopleCount, 10) || 0
    const v = parseInt(g.bVehicleCount, 10) || 0
    if (p === 0 && v === 0) return
    const sid = g.GateSite ? g.GateSite.id : 'none'
    if (!siteMap[sid]) siteMap[sid] = { id: sid, name: (g.GateSite && g.GateSite.sName) || 'Unassigned', people: 0, vehicles: 0, gates: [] }
    siteMap[sid].people += p
    siteMap[sid].vehicles += v
    siteMap[sid].gates.push({ id: g.id, name: g.sName, people: p, vehicles: v })
  })
  const onsiteSites = Object.values(siteMap).sort((a, b) => b.people - a.people)
  // [86bbumedw] P3 — the site whose gate breakdown is open in the slide-over (re-derived from the
  // live list by id, so a poll refresh keeps it current).
  const detailSite = onsiteDetailId != null ? onsiteSites.find(s => String(s.id) === String(onsiteDetailId)) : null

  return (
    <div className="gw-monitor">
      {/* ---- view switch: Events (live crossings) vs User Log (session activity) ---- */}
      <div className="monitor-tabs">
        <div className="chipfilter" role="tablist" aria-label="Monitor view">
          <button role="tab" aria-selected={view === 'events'} className={view === 'events' ? 'on' : ''} onClick={() => setView('events')}>Events</button>
          <button role="tab" aria-selected={view === 'log'} className={view === 'log' ? 'on' : ''} onClick={() => setView('log')}>User Log</button>
        </div>
      </div>

      { view === 'log' ? (
        <Card flush style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden', flex: '1 1 auto', minHeight: 0 }}>
          <UserLog />
        </Card>
      ) : (
      <>
      {/* ---- On-site now: compact, height-capped, LIVE per-site occupancy (sorted by headcount;
          overflow scrolls, never grows). No grand total (David: not valuable). Click a site for
          its gate breakdown in a slide-over. ---- */}
      <Card pad="sm" className="onsite-now">
        <div className="onsite-now__bar">
          <span className="eyebrow">On-site now</span>
          { onsiteSites.length === 0
            ? <span className="onsite-now__meta">No one currently on-site</span>
            : <div className="onsite-now__pills">
                { onsiteSites.map(s => (
                  <button key={s.id} type="button" className="onsite-pill" onClick={() => setOnsiteDetailId(s.id)}
                    title={`${s.name}: ${s.people} people · ${s.vehicles} vehicles — click for the gate breakdown`}>
                    <span className="onsite-pill__name">{ s.name }</span>
                    <b className="onsite-pill__count num">{ s.people }·{ s.vehicles }</b>
                  </button>
                )) }
              </div>
          }
        </div>
      </Card>

      {/* ---- P3 on-demand detail: a slide-over with the selected site's gate breakdown ---- */}
      <Drawer
        open={onsiteDetailId != null}
        onClose={() => setOnsiteDetailId(null)}
        title={detailSite ? detailSite.name : 'Site'}
        subtitle={detailSite
          ? `${detailSite.people} ${detailSite.people === 1 ? 'person' : 'people'} · ${detailSite.vehicles} ${detailSite.vehicles === 1 ? 'vehicle' : 'vehicles'} on-site`
          : undefined}
        width="min(420px, 92vw)"
        className="onsite-detail"
      >
        { detailSite
          ? <div className="onsite-detail__gates">
              { detailSite.gates.slice().sort((a, b) => b.people - a.people).map(g => (
                <div key={g.id} className="onsite-detail__gate">
                  <span className="onsite-detail__gname" title={g.name}>{ g.name }</span>
                  <span className="onsite-detail__gcount">
                    <b className="num">{ g.people }</b> <small>ppl</small>
                    <span className="sep2" />
                    <b className="num">{ g.vehicles }</b> <small>veh</small>
                  </span>
                </div>
              )) }
            </div>
          : <p className="sub" style={{ padding: '8px 2px' }}>No one is currently on-site at this site.</p>
        }
      </Drawer>

      {/* ---- events table card (toolbar + DataTable + pagination + drawer) ----
          Export CSV + Reset now live inside the events toolbar (RealtimeEvents →
          EventsTable); the CSV state machine + modal below stay here. */}
      <Card flush style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden', flex: '1 1 auto', minHeight: 0 }}>
        <RealtimeEvents toggleReset={toggleReset} reset={reset} toggleDownloadModal={toggleDownloadModal} />
      </Card>
      </>
      )}

      { showDownloadModal && <EventDownload toggleDownloadModal={toggleDownloadModal} /> }

      <CSVLink headers={headers} data={csvDownloadData} filename={`gw-${moment(new Date()).format('MMDDYYYYHHMMss')}.csv`}>
        <span ref={r => csvButtonRef.current = r}></span>
      </CSVLink>
    </div>
  )
}

const mapStateToProps = (state) => {
  return {
    customerId: state.auth.customerId,
    sites: state.site.sites,
    gates: state.gate.gates,
    monitorView: state.event.monitorView,
    eventListLoaded: state.event.eventListLoaded,
    events: state.event.events,
    eventFilters: state.event.eventFilters,
    eventSorting: state.event.eventSorting,
    eventPagination: state.event.eventPagination,
    eventDownloadStatus: state.event.eventDownloadStatus,
    eventDownloadData: state.event.eventDownloadData,
    getOnsiteCountBySite: state.onsite.getOnsiteCountBySite,
    onsiteGateId: state.onsite.onsiteGateId,
    onsiteSiteId: state.onsite.onsiteSiteId,
    currentPeopleOnsite: state.onsite.currentPeopleOnsite,
    currentVehiclesOnsite: state.onsite.currentVehiclesOnsite,
  }
}
export default connect(mapStateToProps, { setEventState, resetEventState, getGates, getEvents })(TransactionMonitor)
